// src/services/supportChatApi.ts
// Client for the server-side support chat endpoint. The frontend posts the
// conversation history and receives a single assistant reply. The DeepSeek
// API key stays on the server and is never handled here.

import { getCurrentSession } from '../lib/auth';
import { applyTenantRequestHeaders } from '../lib/tenantRequestContext';

export type SupportChatWireRole = 'user' | 'assistant';

export type SupportChatWireMessage = {
  role: SupportChatWireRole;
  content: string;
};

export type SupportChatEscalationReason =
  | 'HUMAN_REQUESTED'
  | 'INSUFFICIENT_KNOWLEDGE'
  | 'ACCOUNT_SPECIFIC'
  | 'BUG_REPORT';

export type SupportChatResult = {
  reply: string;
  escalate: boolean;
  escalationReason?: SupportChatEscalationReason | null;
  /** True when enough issue detail exists to create a useful support ticket. */
  ticketReady: boolean;
  /** AI-generated concise ticket subject (when the conversation was ticket-ready). */
  ticketSubject?: string | null;
};

export type SupportChatRequestError = Error & { status?: number; retryAfterMs?: number };

function parseRetryAfter(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, value * 1000);
  const text = String(value || '').trim();
  if (!text) return undefined;
  const seconds = Number(text);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(text);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : undefined;
}

/**
 * POST /api/support/chat
 * Returns the assistant reply plus an escalation flag (fixed reason code only),
 * or throws on network/auth/upstream errors.
 */
export async function sendSupportChat(messages: SupportChatWireMessage[]): Promise<SupportChatResult> {
  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) {
    throw new Error('missing_access_token');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  applyTenantRequestHeaders(headers);

  const response = await fetch('/api/support/chat', {
    method: 'POST',
    headers,
    body: JSON.stringify({ messages }),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(
      (payload && typeof payload === 'object' && payload.error) || `HTTP ${response.status}`,
    ) as SupportChatRequestError;
    error.status = response.status;
    const payloadRetryAfter = payload && typeof payload === 'object'
      ? payload.retryAfterMs ?? payload.retryAfterSeconds ?? payload.retryAfter ?? payload.retry_after
      : undefined;
    error.retryAfterMs = parseRetryAfter(payloadRetryAfter)
      ?? parseRetryAfter(response.headers?.get?.('Retry-After'));
    throw error;
  }

  if (!payload || typeof payload.reply !== 'string' || !payload.reply.trim()) {
    throw new Error('invalid_support_chat_response');
  }

  return {
    reply: payload.reply,
    escalate: Boolean(payload.escalate),
    escalationReason: typeof payload.escalationReason === 'string' ? payload.escalationReason : null,
    ticketReady: Boolean(payload.ticketReady),
    ticketSubject: typeof payload.ticketSubject === 'string' && payload.ticketSubject.trim() ? payload.ticketSubject.trim() : null,
  };
}

export type CreateSupportTicketInput = {
  summary: string;
  escalationReason?: SupportChatEscalationReason | null;
  transcript: SupportChatWireMessage[];
};

/**
 * POST /api/support/tickets
 * Creates a support ticket from an escalated chat conversation and returns the
 * new ticket id, or throws on network/auth/validation failures.
 */
export async function createSupportTicket(input: CreateSupportTicketInput): Promise<{ ticketId: string }> {
  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) {
    throw new Error('missing_access_token');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  applyTenantRequestHeaders(headers);

  const response = await fetch('/api/support/tickets', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      summary: input.summary,
      escalationReason: input.escalationReason || undefined,
      transcript: input.transcript,
    }),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(
      (payload && typeof payload === 'object' && payload.error) || `HTTP ${response.status}`,
    ) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  if (!payload || typeof payload.ticketId !== 'string' || !payload.ticketId.trim()) {
    throw new Error('invalid_ticket_response');
  }

  return { ticketId: payload.ticketId };
}
